import {
    BookablePeriod,
    PortfolioProductCharging,
    PortfolioProductConsumptionMoment,
    ServiceType,
} from '@mews-commander/data-access';
import { LocalizedEnum } from '@mews/framework';
import { SELECT_EMPTY_VALUE } from '@mews-commander/form';
import { UnicodeEntity } from '@mews/enums';

interface PaymentChargingOptions {
    [ServiceType.Additional]: PortfolioProductCharging[];
    [ServiceType.Bookable]: Record<BookablePeriod, PortfolioProductCharging[]>;
}

export const paymentChargingOptions: PaymentChargingOptions = {
    [ServiceType.Additional]: [PortfolioProductCharging.Once],
    [ServiceType.Bookable]: {
        [BookablePeriod.Hour]: [
            PortfolioProductCharging.Once,
            PortfolioProductCharging.PerPerson,
            PortfolioProductCharging.PerTimeUnit,
            PortfolioProductCharging.PerPersonPerTimeUnit,
        ],
        [BookablePeriod.Day]: [
            PortfolioProductCharging.Once,
            PortfolioProductCharging.PerPerson,
            PortfolioProductCharging.PerTimeUnit,
            PortfolioProductCharging.PerPersonPerTimeUnit,
        ],
        [BookablePeriod.Night]: [
            PortfolioProductCharging.Once,
            PortfolioProductCharging.PerPerson,
            PortfolioProductCharging.PerTimeUnit,
            PortfolioProductCharging.PerPersonPerTimeUnit,
        ],
        [BookablePeriod.Month]: [
            PortfolioProductCharging.Once,
            PortfolioProductCharging.PerPerson,
            PortfolioProductCharging.PerTimeUnit,
            PortfolioProductCharging.PerPersonPerTimeUnit,
        ],
    },
};

type ConsumptionMomentValue = PortfolioProductConsumptionMoment | typeof SELECT_EMPTY_VALUE;

const consumptionMoment = {
    none: [
        {
            value: SELECT_EMPTY_VALUE as ConsumptionMomentValue,
            label: UnicodeEntity.Dash,
        },
    ],
    service: [
        {
            value: PortfolioProductConsumptionMoment.ServiceOrderStart,
            label: (
                <LocalizedEnum
                    value={PortfolioProductConsumptionMoment.ServiceOrderStart}
                    prefix="ProductConsumptionMoment"
                />
            ),
        },
        {
            value: PortfolioProductConsumptionMoment.ServiceOrderEnd,
            label: (
                <LocalizedEnum
                    value={PortfolioProductConsumptionMoment.ServiceOrderEnd}
                    prefix="ProductConsumptionMoment"
                />
            ),
        },
    ],
    posting: [
        {
            value: PortfolioProductConsumptionMoment.PostingTimeUnit,
            label: (
                <LocalizedEnum
                    value={PortfolioProductConsumptionMoment.PostingTimeUnit}
                    prefix="ProductConsumptionMoment"
                />
            ),
        },
        {
            value: PortfolioProductConsumptionMoment.NextTimeUnit,
            label: (
                <LocalizedEnum
                    value={PortfolioProductConsumptionMoment.NextTimeUnit}
                    prefix="ProductConsumptionMoment"
                />
            ),
        },
    ],
};

type ConsumptionMomentGroup = (typeof consumptionMoment)[keyof typeof consumptionMoment];

interface PaymentConsumptionTimingMap {
    [ServiceType.Additional]: {
        [PortfolioProductCharging.Once]: ConsumptionMomentGroup;
    };
    [ServiceType.Bookable]: Record<string, Record<PortfolioProductCharging, ConsumptionMomentGroup>>;
}

export const paymentConsumptionTimingMap: PaymentConsumptionTimingMap = {
    [ServiceType.Additional]: {
        [PortfolioProductCharging.Once]: consumptionMoment.none,
    },
    [ServiceType.Bookable]: {
        Hour: {
            [PortfolioProductCharging.Once]: consumptionMoment.service,
            [PortfolioProductCharging.PerPerson]: consumptionMoment.service,
            [PortfolioProductCharging.PerTimeUnit]: consumptionMoment.none,
            [PortfolioProductCharging.PerPersonPerTimeUnit]: consumptionMoment.none,
        },
        Day: {
            [PortfolioProductCharging.Once]: consumptionMoment.service,
            [PortfolioProductCharging.PerPerson]: consumptionMoment.service,
            [PortfolioProductCharging.PerTimeUnit]: consumptionMoment.none,
            [PortfolioProductCharging.PerPersonPerTimeUnit]: consumptionMoment.none,
        },
        Night: {
            [PortfolioProductCharging.Once]: consumptionMoment.service,
            [PortfolioProductCharging.PerPerson]: consumptionMoment.service,
            [PortfolioProductCharging.PerTimeUnit]: consumptionMoment.posting,
            [PortfolioProductCharging.PerPersonPerTimeUnit]: consumptionMoment.posting,
        },
        Month: {
            [PortfolioProductCharging.Once]: consumptionMoment.service,
            [PortfolioProductCharging.PerPerson]: consumptionMoment.service,
            [PortfolioProductCharging.PerTimeUnit]: consumptionMoment.none,
            [PortfolioProductCharging.PerPersonPerTimeUnit]: consumptionMoment.none,
        },
    },
};

export const getPaymentChargingOptions = (serviceType: ServiceType, bookablePeriod: BookablePeriod | undefined) => {
    if (serviceType === ServiceType.Additional) {
        return paymentChargingOptions?.[ServiceType.Additional] ?? [];
    } else if (serviceType === ServiceType.Bookable && bookablePeriod) {
        return paymentChargingOptions?.[serviceType]?.[bookablePeriod] ?? [];
    }
    return [];
};

export const getConsumptionMomentOptions = (
    serviceType: ServiceType,
    bookablePeriod: BookablePeriod | undefined,
    selectedOption: PortfolioProductCharging
) => {
    if (serviceType === ServiceType.Additional) {
        return paymentConsumptionTimingMap[ServiceType.Additional]?.[PortfolioProductCharging.Once] ?? [];
    } else if (serviceType === ServiceType.Bookable && bookablePeriod) {
        return paymentConsumptionTimingMap[serviceType]?.[bookablePeriod]?.[selectedOption] ?? [];
    }
    return [];
};

export const isChargingConsumptionValid = (currentService: {
    serviceType: ServiceType;
    bookablePeriod: BookablePeriod | undefined;
    charging: PortfolioProductCharging;
    consumptionMoment: PortfolioProductConsumptionMoment;
}): boolean => {
    const {
        serviceType,
        bookablePeriod,
        charging: chargingField,
        consumptionMoment: consumptionMomentField,
    } = currentService || {};

    const consumptionMomentMatch = getConsumptionMomentOptions(serviceType, bookablePeriod, chargingField);

    if (consumptionMomentMatch !== consumptionMoment?.none) {
        return Boolean(consumptionMomentField);
    }
    return Boolean(chargingField);
};
